import { Accidental, Renderer } from 'vexflow4';
import Handler from './singleStaveHandler';
import { findWhichStaveMouseOver, getTouchPositionInCanvas } from '../utils/helpers';
import { NoteMap } from './noteMap';
import Stave from './extension/MusiconStave';
import { StaveNote } from './extension/MusiconStaveNote';

export interface Position {
    x: number;
    y: number;
}

export class TouchListener {
    private canvas: HTMLCanvasElement;
    private handler: Handler;
    private timer: any = null;

    constructor(canvas: HTMLCanvasElement, handler: Handler) {
        this.canvas = canvas;
        this.handler = handler;
    }

    public startListening = (): void => {
        this.canvas.addEventListener('touchstart', this.handleEvent, false);
        this.canvas.addEventListener('touchmove', this.handleEvent, false);
        this.canvas.addEventListener('touchend', this.handleEvent, false);
    };

    public stopListening = (): void => {
        this.canvas.removeEventListener('touchstart', this, false);
        this.canvas.removeEventListener('touchmove', this, false);
        this.canvas.removeEventListener('touchend', this, false);
    };

    public handleEvent = (event: TouchEvent) => {
        // 多点触控情况下保持原始事件
        if (event.touches.length > 1 || event.changedTouches.length > 1) {
            return;
        } else {
            event.preventDefault();
        }

        switch (event.type) {
            case 'touchstart':
                this.handleTouchStart(event);
                break;
            case 'touchmove':
                this.handleTouchMove(event);
                break;
            case 'touchend':
                this.handleTouchEnd(event);
                break;
            default:
                break;
        }
    };

    private handleTouchStart = (evt: TouchEvent) => {
        evt.preventDefault();
        this.timer = setTimeout(() => {
            this.timer = 0;
        }, 100);
        this.showCurrentNote(evt);
    };
    private handleTouchMove = (evt: TouchEvent) => {
        evt.preventDefault();
        this.dragAndDrop(evt);
    };

    private handleTouchEnd = (evt: TouchEvent) => {
        evt.preventDefault();
        clearTimeout(this.timer);

        if (this.timer !== 0) {
            this.showCurrentNote(evt);
        } else {
            this.insertNewNote(evt);
        }
    };

    public findWhichNoteMouseOver = (
        currentStave: Stave | null,
        mousePos: Position,
    ): StaveNote | null => {
        if (!currentStave) return null;

        const allNotes = currentStave.getTickables();

        const curNote =
            allNotes.filter((note) => {
                const { x, y, w, h } = note.getBoundingBox();

                return this.isCursorWithinRectangle(x, y, w, h, mousePos.x, mousePos.y);
            })[0] ?? null;

        return curNote;
    };

    private isCursorWithinRectangle = (
        x: number,
        y: number,
        width: number,
        height: number,
        mouseX: number,
        mouseY: number,
    ) => {
        if (mouseX > x && mouseX < x + width) {
            return true;
        }
        return false;
    };

    private showCurrentNote = (evt: TouchEvent) => {
        this.handler.redraw();
        this.handler.setDragCursorX(null);
        this.handler.setProvisoryTickable(null);
        const pos = {
            x: evt.changedTouches[0].clientX,
            y: evt.changedTouches[0].clientY,
        };

        if (this.handler.isHeadStave(pos)) {
            return;
        }

        const mousePos = getTouchPositionInCanvas(this.canvas, pos);
        const currentStave = this.handler.currentStave;

        const staveList = this.handler.getStaveList();

        const stave = findWhichStaveMouseOver(staveList, mousePos);

        this.handler.setCurrentStave(stave);

        const note = this.findWhichNoteMouseOver(currentStave, mousePos);

        this.handler.setCurrentNote(note);
        this.handler.redraw();
    };

    private insertNewNote = (evt: TouchEvent) => {
        const questionType = localStorage.getItem('question_type');
        const singleBeat = questionType !== 'YinJie';
        this.handler.setDragCursorX(null);
        this.handler.setProvisoryTickable(null);

        const pos = {
            x: evt.changedTouches[0].clientX,
            y: evt.changedTouches[0].clientY,
        } as Position;

        const mousePos = getTouchPositionInCanvas(this.canvas, pos);

        const currentStave = this.handler.getCurrentStave();
        // const currentNote = this.handler.getCurrentNote();
        // 限制只在第一个键位上插入
        const currentNote = singleBeat ? (currentStave?.getNotes().find((_, i) => i === 0) ?? null) : this.handler.getCurrentNote();

        if (!currentStave) return;

        // 插入空白处
        if (currentNote == null) {
            const previousNote = this.handler.getLastTickableBeforeXPosition(mousePos.x);

            const nextNote = this.handler.getFirstTickableAfterXPosition(pos.x);

            const newNote = this.handler.getProvisoryTickable()?.clone({});

            if (newNote == null) return;

            this.addModifiers(newNote, 0);

            currentStave.insertTickableBetween(newNote, previousNote, nextNote);
            // 插入成功之后清除光标
        } else {
            const clickedKeyName = NoteMap.getNoteName(currentStave, mousePos);

            const keyIndex = currentNote.indexOfKey(clickedKeyName);

            if (keyIndex !== -1) {
                return this.handler.redraw();
            }

            const newKeys = currentNote.getKeys();
            newKeys.push(clickedKeyName);
            const oldNote = currentNote;
            const newNote = oldNote.clone({
                keys: newKeys,
                modifiers: currentNote.getModifiers(),
            });
            const modifierIndex = newKeys.indexOf(clickedKeyName);

            this.addModifiers(newNote, modifierIndex);

            currentStave.replaceTickable(oldNote, newNote);
            this.handler.setCurrentNote(newNote);
        }

        // console.log(notes.map((note: StaveNote) => note.getKeys().join(' ')));

        this.handler.afterInsertNoteUpStatusCallback();
        this.handler.afterInsertNoteDownStatusCallback();
        this.handler.afterInsertRenoteStatusCallback();
        this.handler.afterInsertNoteDoubleUpStatusCallback();
        this.handler.afterInsertNoteDoubleDownStatusCallback();

        this.handler.redraw();
    };

    private dragAndDrop = (evt: TouchEvent) => {
        this.handler.redraw();
        // 手指拖拽显示偏移
        const questionType = localStorage.getItem('question_type');
        const x = questionType === 'YinJie' ?
            (evt.changedTouches[0].clientX - 30) : evt.changedTouches[0].clientX / 1.4;
        const pos = {
            x,
            y: evt.changedTouches[0].clientY,
        };
        if (this.handler.isHeadStave(pos)) {
            return;
        }

        const mousePos = getTouchPositionInCanvas(this.canvas, pos);
        const staveList = this.handler.getStaveList();
        const currentStave = this.handler.currentStave;

        const stave = findWhichStaveMouseOver(staveList, mousePos);

        this.handler.setCurrentStave(stave);

        const note = this.findWhichNoteMouseOver(currentStave, mousePos);

        this.handler.setCurrentNote(note);
        this.handler.updateProvisoryKey(mousePos);
    };

    private addModifiers = (newNote: StaveNote, index: number = 0) => {
        if (this.handler.getNoteUpStatus() === 1) {
            newNote.addModifier(new Accidental('#'), index);
            this.handler.setNoteUpStatus(0);
        } else if (this.handler.getNoteUpStatus() === 2) {
            newNote.addModifier(new Accidental('#'), index);
        }

        if (this.handler.getNoteDownStatus() === 1) {
            newNote.addModifier(new Accidental('b'), index);
            this.handler.setNoteDownStatus(0);
        } else if (this.handler.getNoteDownStatus() === 2) {
            newNote.addModifier(new Accidental('b'), index);
        }

        if (this.handler.getNoteDoubleUpStatus() === 1) {
            newNote.addModifier(new Accidental('##'), index);
            this.handler.setNoteDoubleUpStatus(0);
        } else if (this.handler.getNoteDoubleUpStatus() === 2) {
            newNote.addModifier(new Accidental('##'), index);
        }

        if (this.handler.getNoteDoubleDownStatus() === 1) {
            newNote.addModifier(new Accidental('bb'), index);
            this.handler.setNoteDoubleDownStatus(0);
        } else if (this.handler.getNoteDoubleDownStatus() === 2) {
            newNote.addModifier(new Accidental('bb'), index);
        }

        if (this.handler.getRenoteStatus() === 1) {
            newNote.addModifier(new Accidental('n'), index);
            this.handler.setRenoteStatus(0);
        } else if (this.handler.getRenoteStatus() === 2) {
            newNote.addModifier(new Accidental('n'), index);
        }
    };
}
